if (!customElements.get('key-benefits')) {
  customElements.define(
    'key-benefits',
    class KeyBenefits extends HTMLElement {
      connectedCallback() {
        this.AUTO_ROTATE_DURATION = 5500;
        this._activeIndex = 0;
        this._autoRotateTimer = null;
        this._programmaticSlide = false;
        this._skipAutoRotate = true; // prevent auto-rotate during init
        this._isPaused = false;
        this._remainingTime = this.AUTO_ROTATE_DURATION;
        this._scheduleStart = null;
        this._prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // Set CSS duration custom property so progress animation matches the timer
        this.style.setProperty('--kb-duration', this.AUTO_ROTATE_DURATION + 'ms');

        this.benefits = Array.from(this.querySelectorAll('.key-benefits__benefit-list .key-benefits__benefit'));
        this.images = Array.from(this.querySelectorAll('.key-benefits__circle-image, .key-benefits__circle-image-placeholder'));
        this.slides = Array.from(this.querySelectorAll('.key-benefits__slide'));

        this.handleClick = this.handleClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleMouseEnter = this.handleMouseEnter.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
        this.handleFocusIn = this.handleFocusIn.bind(this);
        this.handleFocusOut = this.handleFocusOut.bind(this);

        this.addEventListener('click', this.handleClick);
        this.addEventListener('keydown', this.handleKeydown);
        this.addEventListener('mouseenter', this.handleMouseEnter);
        this.addEventListener('mouseleave', this.handleMouseLeave);
        this.addEventListener('focusin', this.handleFocusIn);
        this.addEventListener('focusout', this.handleFocusOut);

        // Activate first benefit without triggering auto-rotate restart
        this.activateBenefit(0, { moveFocus: false });
        this._skipAutoRotate = false;

        this.initSwiper();

        // Start progress + auto-rotate when section scrolls into view
        if (!this._prefersReducedMotion) {
          this._setupViewportObserver();
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('keydown', this.handleKeydown);
        this.removeEventListener('mouseenter', this.handleMouseEnter);
        this.removeEventListener('mouseleave', this.handleMouseLeave);
        this.removeEventListener('focusin', this.handleFocusIn);
        this.removeEventListener('focusout', this.handleFocusOut);
        this.stopAutoRotate();
        this._viewportObserver?.disconnect();

        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }

        if (this._swiperMQ) {
          if (this._swiperMQ.removeEventListener) {
            this._swiperMQ.removeEventListener('change', this._onMQChange);
          } else {
            this._swiperMQ.removeListener(this._onMQChange);
          }
        }
      }

      handleClick(event) {
        const benefit = event.target.closest('.key-benefits__benefit');

        if (!benefit || !this.contains(benefit)) return;

        const index = this.benefits.indexOf(benefit);

        if (index === -1) return;

        this.activateBenefit(index, { moveFocus: false });
      }

      handleKeydown(event) {
        const current = event.target.closest('.key-benefits__benefit');

        if (!current) return;

        const currentIndex = this.benefits.indexOf(current);
        let next = null;

        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            next = (currentIndex + 1) % this.benefits.length;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            next = (currentIndex - 1 + this.benefits.length) % this.benefits.length;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = this.benefits.length - 1;
            break;
          default:
            return;
        }

        event.preventDefault();
        this.activateBenefit(next, { moveFocus: true });
      }

      handleMouseEnter() {
        this.pauseAutoRotate();
      }

      handleMouseLeave() {
        this.resumeAutoRotate();
      }

      handleFocusIn() {
        this.pauseAutoRotate();
      }

      handleFocusOut(event) {
        // Only resume if focus has left the component entirely
        if (!this.contains(event.relatedTarget)) {
          this.resumeAutoRotate();
        }
      }

      activateBenefit(index, { moveFocus }) {
        this._activeIndex = index;

        // Drive the CSS indicator via custom property
        this.style.setProperty('--key-benefits-active-index', index);

        // Update desktop benefit items
        this.benefits.forEach((benefit, i) => {
          const isActive = i === index;
          benefit.classList.toggle('is-active', isActive);
          benefit.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          benefit.setAttribute('tabindex', isActive ? '0' : '-1');

          if (isActive && moveFocus) {
            benefit.focus();
          }
        });

        // Update circle images (crossfade)
        this.images.forEach((img, i) => {
          img.classList.toggle('is-active', i === index);
        });

        // Update mobile slide active class
        this.slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === index);
        });

        // Sync mobile Swiper without triggering the slideChange callback loop
        if (this.swiper && !this._programmaticSlide) {
          this._programmaticSlide = true;
          this.swiper.slideTo(index);
          requestAnimationFrame(() => {
            this._programmaticSlide = false;
          });
        }

        // Restart auto-rotate and progress on every interaction
        if (!this._skipAutoRotate) {
          this.stopAutoRotate();
          this._isPaused = false;

          if (!this._prefersReducedMotion) {
            this.resetProgress();
            // Only schedule next if not currently paused by hover/focus or viewport
            if (!this._hoverFocusPaused && !this._viewportPaused) {
              this.scheduleNext();
            }
          }
        }
      }

      // Schedule the next auto-rotation — always X seconds from the last change
      scheduleNext(delay = this.AUTO_ROTATE_DURATION) {
        this._scheduleStart = Date.now();
        this._scheduledDelay = delay;
        this._autoRotateTimer = setTimeout(() => {
          const next = (this._activeIndex + 1) % this.benefits.length;
          this.activateBenefit(next, { moveFocus: false });
        }, delay);
      }

      stopAutoRotate() {
        if (this._autoRotateTimer) {
          clearTimeout(this._autoRotateTimer);
          this._autoRotateTimer = null;
        }
      }

      pauseAutoRotate() {
        if (this._prefersReducedMotion || this._hoverFocusPaused) return;

        this._hoverFocusPaused = true;

        // Calculate remaining time before the next rotation
        if (this._autoRotateTimer && this._scheduleStart !== null) {
          const elapsed = Date.now() - this._scheduleStart;
          this._remainingTime = Math.max(0, this._scheduledDelay - elapsed);
        }

        this.stopAutoRotate();

        // Pause CSS progress animations
        this.querySelectorAll('.key-benefits__indicator-fill, .key-benefits__pagination-fill').forEach(fill => {
          fill.style.animationPlayState = 'paused';
        });
      }

      resumeAutoRotate() {
        if (!this._hoverFocusPaused) return;

        this._hoverFocusPaused = false;

        // Resume CSS progress animations
        this.querySelectorAll('.key-benefits__indicator-fill, .key-benefits__pagination-fill').forEach(fill => {
          fill.style.animationPlayState = 'running';
        });

        // Schedule next rotation with the remaining time
        if (!this._prefersReducedMotion) {
          this.scheduleNext(this._remainingTime);
        }
      }

      // Restart the CSS progress animation on the indicator fill (desktop)
      // and the pagination fill (mobile)
      resetProgress() {
        this.querySelectorAll('.key-benefits__indicator-fill, .key-benefits__pagination-fill').forEach(fill => {
          fill.style.animationPlayState = '';
          fill.classList.remove('is-animating');
          void fill.offsetWidth; // force reflow so the animation restarts from 0
          fill.classList.add('is-animating');
        });
        this._remainingTime = this.AUTO_ROTATE_DURATION;
      }

      _setupViewportObserver() {
        let started = false;
        this._viewportObserver = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              if (!started) {
                started = true;
                this.resetProgress();
                this.scheduleNext();
              } else if (this._viewportPaused) {
                this._viewportPaused = false;
                if (!this._hoverFocusPaused) {
                  this.querySelectorAll('.key-benefits__indicator-fill, .key-benefits__pagination-fill').forEach(fill => {
                    fill.style.animationPlayState = 'running';
                  });
                  this.scheduleNext(this._remainingTime);
                }
              }
            } else if (started && !this._viewportPaused) {
              this._viewportPaused = true;
              if (this._autoRotateTimer && this._scheduleStart !== null) {
                const elapsed = Date.now() - this._scheduleStart;
                this._remainingTime = Math.max(0, this._scheduledDelay - elapsed);
              }
              this.stopAutoRotate();
              this.querySelectorAll('.key-benefits__indicator-fill, .key-benefits__pagination-fill').forEach(fill => {
                fill.style.animationPlayState = 'paused';
              });
            }
          },
          { threshold: 0 }
        );
        this._viewportObserver.observe(this);

        // Safari sometimes skips the initial IntersectionObserver callback —
        // start immediately if the element is already in the viewport.
        const rect = this.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          started = true;
          this.resetProgress();
          this.scheduleNext();
        }
      }

      initSwiper() {
        const container = this.querySelector('.key-benefits__swiper');

        if (!container) return;

        this._swiperMQ = window.matchMedia('(max-width: 749px)');

        this._onMQChange = (event) => {
          if (event.matches) {
            this._createSwiper(container);
          } else if (this.swiper) {
            this.swiper.destroy(true, true);
            this.swiper = null;
          }
        };

        if (this._swiperMQ.addEventListener) {
          this._swiperMQ.addEventListener('change', this._onMQChange);
        } else {
          this._swiperMQ.addListener(this._onMQChange);
        }

        if (this._swiperMQ.matches) {
          this._createSwiper(container);
        }
      }

      _createSwiper(container) {
        if (typeof Swiper === 'undefined') return;

        this.swiper = new Swiper(container, {
          slidesPerView: 'auto',
          centeredSlides: true,
          initialSlide: 0,
          spaceBetween: 16,
          keyboard: {
            enabled: true,
            onlyInViewport: true,
          },
          a11y: {
            enabled: true,
            prevSlideMessage: 'Previous benefit',
            nextSlideMessage: 'Next benefit',
          },
          on: {
            // Only fires on user swipe — programmatic slides are guarded above
            slideChange: (swiper) => {
              if (this._programmaticSlide) return;
              this.activateBenefit(swiper.activeIndex, { moveFocus: false });
            },
          },
        });
      }
    }
  );
}
